import {createStyles} from '@mantine/core';
import React from 'react';
import {Handle, Node, NodeProps} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {defaultHandleProps} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeHandleUtils';

import {FALLBACK_NODE_HEIGHT} from './ProcedureRunDesignFlowchartNode';
import {getProcedureRunDesignFlowchartNodeHandleId} from './ProcedureRunDesignFlowchartNodeHandle';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_GHOST_NODE_TYPE = 'procedureRunDesignFlowchartGhostNode';

export interface ProcedureRunDesignFlowchartGhostNodeData {
  /** Method whose column hosts this placeholder. */
  methodId: string;
}

/** Node id for a ghost placeholder. */
export function getProcedureRunDesignFlowchartGhostNodeId(methodId: string): string {
  return `ghost-${methodId}`;
}

export function createProcedureRunDesignFlowchartGhostNode({
  methodId,
}: {
  methodId: string;
}): Node<ProcedureRunDesignFlowchartGhostNodeData> {
  return {
    id: getProcedureRunDesignFlowchartGhostNodeId(methodId),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_GHOST_NODE_TYPE,
    data: {methodId},
    position: {x: 0, y: 0},
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartGhostNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartGhostNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_GHOST_NODE_TYPE;
}

/** An empty placeholder box previewing where a new replicate would appear. */
export default function ProcedureRunDesignFlowchartGhostNode(
  props: NodeProps<ProcedureRunDesignFlowchartGhostNodeData>
): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.card}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartGhostNode', element: 'root'})}
    >
      <Handle
        {...defaultHandleProps('target')}
        id={getProcedureRunDesignFlowchartNodeHandleId(props.id, 'target')}
        isConnectable={false}
        className={classes.hiddenHandle}
      />
      <Handle
        {...defaultHandleProps('source')}
        id={getProcedureRunDesignFlowchartNodeHandleId(props.id, 'source')}
        isConnectable={false}
        className={classes.hiddenHandle}
      />
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  card: {
    boxSizing: 'border-box',
    width: COMMON_NODE_WIDTH,
    height: FALLBACK_NODE_HEIGHT,
    border: `1px solid ${theme.colors.blue[2]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.blue[0],
  },
  hiddenHandle: {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    opacity: 0,
    border: 'none',
    background: 'transparent',
    pointerEvents: 'none',
  },
}));
